import { AnalyticsPanel } from "./AnalyticsPanel";
import { useEffect, useState } from "react";
import { installSortableTables } from "./sortableTables";
import type { User } from "firebase/auth";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";
import { SignIn } from "./SignIn";
import { getMe, type Me, getTiles, type Tiles, getEnvironment, setEnvironment, type Environment } from "./actions";
import { SheetHome } from "./SheetHome";
import { CategoriesPanel } from "./CategoriesPanel";
import { ArchivePanel } from "./ArchivePanel";
import { FlaggedPanel } from "./FlaggedPanel";
import { FeedHealthPanel } from "./FeedHealthPanel";
import { AccessPanel } from "./AccessPanel";
import { NavIcon } from "./NavIcon";
import { ProfilePanel } from "./ProfilePanel";
import { CustomersPanel } from "./CustomersPanel";
import { FraudPanel } from "./FraudPanel";
import { StatusPanel } from "./StatusPanel";
import { RequestsPanel } from "./RequestsPanel";
import { ExclusionsPanel } from "./ExclusionsPanel";
import { FeedMappingPanel } from "./FeedMappingPanel";
import { RowIssuesPanel } from "./RowIssuesPanel";
import { FeedReviewPanel } from "./FeedReviewPanel";
import { DataHealthPanel } from "./DataHealthPanel";
import { ShippingMatrixPanel } from "./ShippingMatrixPanel";
import { PendingEditsPanel } from "./PendingEditsPanel";

// Tile grid, same visual language as DLA BUS's own console (capgrid/stat tiles) —
// report the actual SET behind a number, never a bare percentage. See
// backend/api/main.py's op_tiles for what's real vs. still pending.

function Tile({ label, value, tone, onClick }: {
  label: string; value: string; tone?: "warn" | "bad"; onClick?: () => void;
}) {
  // A real <button> when clickable -- not a div with onClick, which is invisible
  // to accessibility tools and keyboard navigation alike (found while verifying
  // this in the browser: the accessibility tree reported zero interactive
  // elements on a page with two clickable tiles).
  const Tag = onClick ? "button" : "div";
  return (
    <Tag className={`tile ${tone ?? ""}`} onClick={onClick} aria-label={onClick ? label : undefined}>
      <div className="tile-value">{value}</div>
      <div className="tile-label">{label}</div>
    </Tag>
  );
}

// Gates the whole app behind Firebase Auth (ops/CONSOLE_AUTH_SCOPE.md) --
// matches DLA BUS's own `if (!user) return <SignIn/>;` shape. A signed-in
// user still needs a real console_principals record server-side to
// actually DO anything (backend/api/main.py refuses otherwise) -- this only
// gates whether the UI renders at all.
export function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);

  useEffect(() => onAuthStateChanged(auth, setUser), []);
  useEffect(() => installSortableTables(), []);

  if (user === undefined) return <div className="panel">Loading…</div>;
  if (user === null) return <SignIn />;

  return <AuthenticatedApp userEmail={user.email ?? ""} />;
}

// There is ONE console. Which environment it is showing is chosen in the sidebar and is always visible: a banner
// on every page names it, and the console environment is a different colour from the sandbox.

const NAV: { key: TabKey; label: string; cap?: string }[] = [
  { key: "dashboard", label: "Dashboard", cap: "view.dashboard" },
  { key: "sheet", label: "Sheet", cap: "view.sheet" },
  { key: "categories", label: "Categories", cap: "view.sheet" },
  { key: "archive", label: "Production archive", cap: "view.sheet" },
  { key: "health", label: "Feed Health", cap: "view.feed_health" },
  { key: "mapping", label: "Feed Mapping", cap: "view.feed_mapping" },
  { key: "requests", label: "Requests", cap: "view.requests" },
  { key: "status", label: "Status", cap: "view.feed_health" },
  { key: "analytics", label: "Analytics", cap: "view.analytics" },
  { key: "customers", label: "Customers", cap: "view.customers" },
  { key: "fraud", label: "Fraud Analyzer", cap: "view.customers" },
  { key: "exclusions", label: "Exclusions", cap: "view.exclusions" },
  { key: "datahealth", label: "Data Health", cap: "view.data_health" },
  { key: "shipping", label: "Shipping Matrix", cap: "view.shipping" },
  { key: "pendingedits", label: "Pending Edits", cap: "view.pending_edits" },
  { key: "access", label: "Users and Access", cap: "admin" },
  { key: "profile", label: "Profile" },
];

type TabKey = "analytics" | "dashboard" | "sheet" | "categories" | "archive" | "mapping" | "exclusions" | "pricing" | "access" | "requests" | "status" | "customers" | "fraud" | "profile" | "health" | "datahealth" | "shipping" | "pendingedits";

function AuthenticatedApp({ userEmail }: { userEmail: string }) {
  const [tab, setTab] = useState<TabKey>("dashboard");
  const [navOpen, setNavOpen] = useState(false);
  const [tiles, setTiles] = useState<Tiles | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flagged, setFlagged] = useState<string | null>(null);   // issue type whose flagged rows are open
  const [env, setEnv] = useState<Environment>(getEnvironment());
  const [me, setMe] = useState<Me | null>(null);
  useEffect(() => { getMe().then((r) => { if (r.ok) setMe(r.data); }); }, [env]);
  const allowed = (cap?: string) => !cap || (me?.class === "admin") || (cap !== "admin" && !!me?.caps.includes(cap));
  const visibleNav = NAV.filter((s) => me && allowed(s.cap));
  useEffect(() => {      // land on the first page this person may use
    if (me && visibleNav.length && !visibleNav.some((s) => s.key === tab)) setTab(visibleNav[0].key);
  }, [me]);

  useEffect(() => {
    setTiles(null);
    setError(null);
    getTiles().then((r) => {
      if (r.ok) { setTiles(r.data); return; }
      // Opens in the console environment; someone without that capability is moved to the sandbox, not shown an error.
      if (env === "console" && /may not use the console environment/.test(r.error)) {
        setEnvironment("sandbox");
        setEnv("sandbox");
        return;
      }
      setError(r.error);
    });
  }, [env]);

  const switchEnvironment = (next: Environment) => {
    if (next === env) return;
    if (next === "console" && !window.confirm("Switch to the CONSOLE environment? This is the real catalog data; changes here are real.")) return;
    setEnvironment(next);
    setEnv(next);
  };

  const fillView = tab === "sheet" || tab === "categories" || tab === "archive" || tab === "mapping" || tab === "exclusions" || tab === "requests" || tab === "customers" || tab === "health" || (tab === "dashboard" && !!flagged);   // these views fit the window and scroll inside

  const panel = () => {
    switch (tab) {
      case "pendingedits": return <PendingEditsPanel />;
      case "shipping": return <ShippingMatrixPanel />;
      case "datahealth": return <DataHealthPanel />;
      case "sheet": return <SheetHome />;
      case "archive": return <ArchivePanel key={`${env}-archive`} />;
      case "categories": return <CategoriesPanel key={`${env}-categories`} />;
      case "health": return <FeedHealthPanel key={`${env}-health`} />;
      case "status": return <StatusPanel key={`${env}-status`} />;
      case "analytics": return <AnalyticsPanel key={`${env}-analytics`} />;
      case "customers": return <CustomersPanel key={`${env}-customers`} canWrite={allowed("customers.write")} canExport={allowed("customers.export")} />;
      case "fraud": return <FraudPanel />;
      case "requests": return <RequestsPanel key={`${env}-requests`} />;
      case "profile": return <ProfilePanel email={userEmail} me={me} environment={env} />;
      case "access": return <AccessPanel key="access" myEmail={userEmail} />;
      case "pricing": return <SheetHome key={`${env}-pricing`} initial="policy" />;
      case "exclusions": return <ExclusionsPanel key={`${env}-exclusions`} />;
      case "mapping": return <FeedMappingPanel key={`${env}-mapping`} />;
      default: return flagged === "row_red" || flagged === "row_orange" || flagged === "row_rejected"
        ? <RowIssuesPanel key={`${env}-${flagged}`} severity={flagged.slice(4) as "red" | "orange" | "rejected"} />
        : flagged === "feed_review"
        ? <FeedReviewPanel key={`${env}-feed`} />
        : flagged
        ? <FlaggedPanel key={`${env}-${flagged}`} issueType={flagged} label={FLAG_LABELS[flagged] ?? flagged} />
        : <Dashboard tiles={tiles} error={error} onOpenFlagged={setFlagged} onOpenTab={(t) => { setFlagged(null); setTab(t); }} />;
    }
  };

  return (
    <div className={`shell${navOpen ? " nav-open" : ""}${fillView ? " fill" : ""}`}>
      <aside className="sidebar">
        <div className="side-brand">
          <b>HEC Console</b>
          <span>environment · {env}</span>
          <label className="env-switch">
            Environment
            <select value={env} onChange={(e) => switchEnvironment(e.target.value as Environment)}>
              <option value="sandbox">Sandbox</option>
              <option value="console">Console</option>
            </select>
          </label>
        </div>
        <nav>
          {visibleNav.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`navrow${tab === s.key ? " on" : ""}`}
              onClick={() => { setTab(s.key); setFlagged(null); setNavOpen(false); }}
            >
              <NavIcon name={s.key} />
              <span>{s.label}</span>
            </button>
          ))}
        </nav>
        <div className="side-foot">
          <div className="me-id" role="button" tabIndex={0} style={{ cursor: "pointer" }} title="Your profile and settings"
               onClick={() => { setTab("profile"); setFlagged(null); setNavOpen(false); }}>
            <span className="me-name">{userEmail}</span>
            <span className="me-sub">Signed in</span>
          </div>
          <button type="button" onClick={() => signOut(auth)}>Sign out</button>
        </div>
      </aside>

      <main>
        <div className={`env-banner${env === "console" ? " env-console" : ""}`} role="status">
          {env === "console"
            ? "CONSOLE — the real catalog data. Changes here are real."
            : "SANDBOX — isolated test data"}
        </div>
        <button className="burger burger-float" style={{ display: "none" }} onClick={() => setNavOpen((v) => !v)} aria-label="Menu">≡</button>
        <div className={`main-inner${fillView ? " fill" : ""}`} key={env}>
          {panel()}
        </div>
      </main>

      {navOpen && <div className="scrim" onClick={() => setNavOpen(false)} />}
    </div>
  );
}

const FLAG_LABELS: Record<string, string> = {
  status_stock_but_zero_quantity: 'Classed "Stock" but qty=0 (out of stock now)',
  status_nonstock_but_positive_quantity: 'Classed "Non-Stock" but qty>0 (available now)',
  price_below_wholesale_cost: "Feed price below wholesale cost",
};

function Dashboard({ tiles, error, onOpenFlagged, onOpenTab }: {
  tiles: Tiles | null; error: string | null; onOpenFlagged: (issueType: string) => void; onOpenTab: (t: TabKey) => void;
}) {
  if (error) return <div className="panel error">Error: {error}</div>;
  if (!tiles) return <div className="panel">Loading…</div>;

  // NDC's item label against its quantity, counted live on the products (not from the old flagged rows). They are expected by nature (an item class is not availability): shown, not decisions.
  const stockButZero = tiles.labelVsQuantity.stockButZero;
  const nonstockButPositive = tiles.labelVsQuantity.nonStockButPositive;
  const needsReview = tiles.flaggedTotal;

  return (
    <>
      <div className="tilegrid">
        <Tile label="Catalog records (every vendor product, in stock or not; not the sellable count)" value={tiles.productsTotal.toLocaleString()} />
        <Tile
          label={`In stock: quantity above 0, exclusions left out (${tiles.inStockExcluded.toLocaleString()} excluded in stock; the same count as the Sheet)`}
          value={tiles.inStockByQuantity.toLocaleString()}
        />
        <Tile
          label='Classed "Stock" by the vendor (an item class, not availability)'
          value={tiles.inStockByStatusLabel.toLocaleString()}
        />
        <Tile label={`Open requests${tiles.requests?.overdue ? ` (${tiles.requests.overdue} overdue)` : ""}`}
              value={tiles.requests?.open == null ? "n/a" : tiles.requests.open.toLocaleString()}
              tone={tiles.requests?.overdue ? "bad" : tiles.requests?.open ? "warn" : undefined}
              onClick={() => onOpenTab("requests")} />
        <Tile
          label={`Flagged for review (${needsReview.toLocaleString()}): the two label-versus-quantity types below are not counted`}
          value={needsReview.toLocaleString()}
          tone={needsReview > 0 ? "warn" : undefined}
        />
        <Tile
          label='NDC: classed "Stock" but qty=0, out of stock now (live count)'
          value={stockButZero.toLocaleString()}
        />
        <Tile
          label='NDC: classed "Non-Stock" but qty>0, in stock, quantity decides (live count)'
          value={nonstockButPositive.toLocaleString()}
        />
        <Tile
          label="Feed problems (critical): kept, flagged red"
          value={(tiles.rowIssues?.red ?? 0).toLocaleString()}
          tone={(tiles.rowIssues?.red ?? 0) > 0 ? "bad" : undefined}
          onClick={() => onOpenFlagged("row_red")}
        />
        <Tile
          label="Feed problems (soft): kept, flagged orange"
          value={(tiles.rowIssues?.orange ?? 0).toLocaleString()}
          tone={(tiles.rowIssues?.orange ?? 0) > 0 ? "warn" : undefined}
          onClick={() => onOpenFlagged("row_orange")}
        />
        <Tile
          label="Rows the ingest could not store"
          value={(tiles.rowIssues?.rejected ?? 0).toLocaleString()}
          tone={(tiles.rowIssues?.rejected ?? 0) > 0 ? "bad" : undefined}
          onClick={() => onOpenFlagged("row_rejected")}
        />
        <Tile
          label={`Feed mistakes${tiles.feedFindingsHigh ? ` (${tiles.feedFindingsHigh} high)` : ""}`}
          value={(tiles.feedFindingsTotal ?? 0).toLocaleString()}
          tone={tiles.feedFindingsHigh > 0 ? "warn" : undefined}
          onClick={() => onOpenFlagged("feed_review")}
        />
        <Tile
          label="Feed price below wholesale cost"
          value={(tiles.flaggedByType["price_below_wholesale_cost"] ?? 0).toLocaleString()}
          tone="warn"
          onClick={() => onOpenFlagged("price_below_wholesale_cost")}
        />
      </div>

      <div className="panel">
        <p className="meta" style={{ marginTop: 0 }}>
          Quantity above 0 decides what is in stock and sellable. The vendor's "Stock" / "Non-Stock" label is an item classification (stocked item or special-order item), not availability,
          so the two counts above are not two answers to one question; they differ on {(stockButZero + nonstockButPositive).toLocaleString()} SKUs by nature: a stocked item that is out
          of stock now, and a non-stock item that has a quantity available.
        </p>
        <p className="meta">
          Click a flagged tile to open its rows in a table and review them here. Each table has an Export button that
          downloads a CSV only; sending anything to NDC support or anyone else is a separate, deliberate human step.
        </p>
      </div>
    </>
  );
}
